import { IComponentDefinition, IDisplayItemComponent } from "@kuika/kuika-cl-model";
import { DefaultStyleService } from "../../../services/default-style-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface DefaultStyleProviderState {
  getDefaultStyles: (componentName: string, isMobile?: boolean) => any;
  getComponentDefaultStyle: (
    component: IDisplayItemComponent,
    componentDefinition?: IComponentDefinition,
    isMobile?: boolean
  ) => any;
  getDesktopStyles: () => any;
  getMobileStyles: () => any;
  getStylesByType: (styleType: "fill" | "border" | "shadow" | "typography" | "position", isMobile?: boolean) => any;
  isInitialized: boolean;
  defaultStyleProvider: DefaultStyleProvider | null;
}

export class DefaultStyleProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  DefaultStyleProviderState
> {
  constructor(context: ScreenDesignContextProvider) {
    super(context);
    this.initialize();
  }

  private desktopStyles: any = null;

  private mobileStyles: any = null;

  private isInitialized = false;

  static defaultState: DefaultStyleProviderState = {
    getDefaultStyles: () => ({}),
    getComponentDefaultStyle: () => ({}),
    getDesktopStyles: () => ({}),
    getMobileStyles: () => ({}),
    getStylesByType: () => ({}),
    isInitialized: false,
    defaultStyleProvider: null
  };

  async initialize() {
    if (this.isInitialized) return;

    try {
      const allStyles = await DefaultStyleService.getAllDefaultStyles();

      this.desktopStyles = {
        fill: allStyles.fill,
        border: allStyles.border,
        shadow: allStyles.shadow,
        typography: allStyles.typography,
        position: allStyles.position
      };

      this.mobileStyles = {
        fill: allStyles.fillMobile,
        border: allStyles.borderMobile,
        shadow: allStyles.shadowMobile,
        typography: allStyles.typographyMobile,
        position: allStyles.positionMobile
      };

      this.isInitialized = true;
      this.setState({ isInitialized: true });
    } catch (error) {
      console.warn("Failed to load default styles during initialization:", error);
    }
  }

  getProviderValues = (): DefaultStyleProviderState => {
    return {
      getDefaultStyles: this.getDefaultStyles,
      getComponentDefaultStyle: this.getComponentDefaultStyle,
      getDesktopStyles: this.getDesktopStyles,
      getMobileStyles: this.getMobileStyles,
      getStylesByType: this.getStylesByType,
      isInitialized: this.isInitialized,
      defaultStyleProvider: this
    };
  };

  private waitForInitialization(): void {
    const startTime = Date.now();
    const timeout = 5000;
    while (!this.isInitialized) {
      if (Date.now() - startTime > timeout) {
        throw new Error("Timeout waiting for default styles initialization");
      }
    }
  }

  getDesktopStyles = (): any => {
    this.waitForInitialization();
    return this.desktopStyles;
  };

  getMobileStyles = (): any => {
    this.waitForInitialization();
    return this.mobileStyles;
  };

  getStylesByType = (
    styleType: "fill" | "border" | "shadow" | "typography" | "position",
    isMobile: boolean = false
  ): any => {
    this.waitForInitialization();
    const styles = isMobile ? this.mobileStyles : this.desktopStyles;
    return styles?.[styleType] || {};
  };

  getDefaultStyles = (componentName: string, isMobile: boolean = false): any => {
    this.waitForInitialization();
    const styles = isMobile ? this.mobileStyles : this.desktopStyles;
    if (!styles) return {};

    const componentStyles: any = {};

    Object.keys(styles).forEach((styleType) => {
      if (styles[styleType] && styles[styleType][componentName]) {
        componentStyles[styleType] = styles[styleType][componentName];
      }
    });

    return componentStyles;
  };

  getComponentDefaultStyle = (
    component: IDisplayItemComponent,
    componentDefinition?: IComponentDefinition,
    isMobile: boolean = false
  ): any => {
    this.waitForInitialization();
    const componentName = componentDefinition?.name || component.name;

    if (!componentName) {
      return {};
    }

    return this.getDefaultStyles(componentName, isMobile);
  };
}
